import type { ImgHTMLAttributes } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from 'helpers/classnames';
import { SmallLoader } from 'components/Loader';
import IntersectionObserver from 'components/common/IntersectionObserver';
import { combineSideEffects } from 'helpers/index';
import { BLANK_IMAGE_PLACEHOLDER } from 'constants/appConstants';

import css from 'styles/components/common/image.scss';

const defaultProps = {
  placeholder: (
    <div className={css.placeholder}>
      <SmallLoader />
    </div>
  ),
  forceLoad: false
};

// remove placeholder global html attribute from the type so its not mistaken as the placeholder for the LazyImage
export type ImageWithoutPlaceholderProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'placeholder'>;

export type WebpImageProps = { webp: { src: string; srcSet: string } };
export type PicturePropsType = { pictureProps?: JSX.Element[] | null };
export type ImageProps = ImageWithoutPlaceholderProps & Partial<typeof defaultProps> & Partial<WebpImageProps> & PicturePropsType;
const Image = ({
  onLoad,
  className,
  placeholder = defaultProps.placeholder,
  forceLoad = defaultProps.forceLoad,
  pictureProps = null,
  ...rest
}: ImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const showPlaceholder = placeholder && !loaded && !forceLoad;
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Handles removing loading class from cached images
    ref && ref.current && ref.current?.complete && setLoaded(true);
  }, []);

  const { srcSet, sizes, webp: { srcSet: webpSrcSet } = {}, ...restProps } = rest;

  return (
    <>
      {showPlaceholder && placeholder}
      <picture>
        {webpSrcSet && <source srcSet={webpSrcSet} sizes={sizes} type="image/webp" />}
        {pictureProps}
        <img
          ref={ref}
          loading={webpSrcSet ? 'lazy' : undefined} // only loading="lazy" img tags if a webp is provided, to avoid both loading simultaneously https://github01.zappos.net/mweb/marty/issues/20852
          alt=""
          className={cn(className, css.image, { [css.loaded]: loaded })}
          srcSet={srcSet}
          sizes={sizes}
          onLoad={combineSideEffects(onLoad, () => setLoaded(true))}
          {...restProps}
        />
      </picture>
    </>
  );
};

export const LazyImage = ({ placeholder, forceLoad, ...rest }: ImageProps) => {
  const placeholderWithEmptyImage = (
    <>
      {placeholder}
      {rest.alt && <img src={BLANK_IMAGE_PLACEHOLDER} className="sr-only" alt={rest.alt} />}{' '}
      {/* hidden blank image place holder for screen readers. */}
    </>
  );
  return (
    <IntersectionObserver placeholder={placeholderWithEmptyImage} forceLoad={forceLoad}>
      <Image placeholder={placeholder} forceLoad={forceLoad} {...rest} />
    </IntersectionObserver>
  );
};

export default Image;
